import React, { useEffect, useMemo, useState } from 'react';
import { IPanelHeaderRenderer, IPanelProps, Panel, PanelType } from '@fluentui/react/lib/Panel';
import { CrLoadingOverlay } from '../cr/CrLoadingOverlay';
import { ViewFormCommandBar } from '../cr/ViewFormCommandBar';
import { IMilestone } from '../../types';
import styles from '../../styles/cr.module.scss';
import { renderDate } from '../cr/ListRenderers';

interface MilestoneViewProps {
    entityName?: string;
    showForm?: boolean;
    entityId?: number;
    onClosed?: () => void;
    loadEntity: (entityId: number) => Promise<IMilestone>;
}

export const MilestoneView: React.FC<MilestoneViewProps> = ({ showForm, entityName, entityId, loadEntity, onClosed }) => {
    const [formData, setFormData] = useState<IMilestone>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const entity = await loadEntity(entityId);
                setFormData(entity);
                setLoading(false);
            } catch (error) {
                console.error('Error loading entity:', error);
                setLoading(false);
            }
        };

        fetchData();
    }, [loadEntity]);

    const milestoneStaff = useMemo(() => {
        if (!formData) return '';

        const staffRoles = [
            { role: 'Lead', users: formData.LeadUser ? [`${formData.LeadUser.Title} (${formData.LeadUser.EmailAddress})`] : [] },
            {
                role: 'Contributors',
                users: formData.Contributors && formData.Contributors.some(x => x.IsReadOnly !== true)
                    ? formData.Contributors.filter(x => x.IsReadOnly !== true).map(mc => `${mc.ContributorUser?.Title} (${mc.ContributorUser?.EmailAddress})`)
                    : [],
            },
            {
                role: 'Read-only contributors',
                users: formData.Contributors && formData.Contributors.some(x => x.IsReadOnly === true)
                    ? formData.Contributors.filter(x => x.IsReadOnly === true).map(mc => `${mc.ContributorUser?.Title} (${mc.ContributorUser?.EmailAddress})`)
                    : [],
            },
        ];

        const formattedStaffRoles = staffRoles
            .filter(role => role.users.length > 0)
            .map(role => `<div style='display:flex'><div style='font-weight:bold'>-&nbsp;</div><div>${role.role}: ${role.users.join(', ')}</div></div>`)
            .join('');

        return formattedStaffRoles;
    }, [formData]);


    const headerText = useMemo(() => {
        if (!formData) return entityName;

        return `${entityName} - ${formData.Title}`;
    }, [entityName, formData]);

    const formattedDescription = useMemo(() => {
        if (!formData) return null;

        return formData.Description ? formData.Description.replace(/\n/g, '<br>') : null;
    }, [formData]);

    return (
        <Panel
            className={styles.cr}
            isOpen={showForm}
            headerText={headerText}
            type={PanelType.medium}
            hasCloseButton={false}
            onRenderHeader={(props: IPanelProps, defaultRender: IPanelHeaderRenderer) =>
                <div style={{ marginBottom: '20px' }} className={styles.panelHeaderText}>{defaultRender(props)}</div>
            }
            onRenderNavigation={() =>
                <ViewFormCommandBar onClose={onClosed} />
            }
        >
            <div className={styles.cr}>
                <CrLoadingOverlay isLoading={loading} opaque={true} />
                {formData && (
                    <div>
                        <div className={styles.viewFormFieldLabel}>Milestone ID</div>
                        <div className={styles.viewFormFieldData}>{formData.MilestoneCode || 'N/A'}</div>

                        <div className={styles.viewFormFieldLabel}>Start-BaselineDelivery Dates</div>
                        <div className={styles.viewFormFieldData}>
                            {renderDate(formData.StartDate)}&nbsp;&nbsp;&nbsp;&nbsp;{renderDate(formData.BaselineDate)}
                        </div>

                        <div className={styles.viewFormFieldLabel}>Staff</div>
                        <div className={styles.viewFormFieldData}>
                            {milestoneStaff ? (
                                <div dangerouslySetInnerHTML={{ __html: milestoneStaff }} />
                            ) : (
                                'N/A'
                            )}
                        </div>

                        <div className={styles.viewFormFieldLabel}>CustomFields</div>
                        <div className={styles.viewFormFieldData}>
                            {formData.CustomFields && formData.CustomFields.length > 0 ? (
                                formData.CustomFields.map(a => a.CustomFieldDfn?.Title).join(', ')
                            ) : (
                                'N/A'
                            )}
                        </div>

                        <div className={styles.viewFormFieldLabel}>Description</div>
                        <div className={styles.viewFormFieldData}>
                            {formattedDescription ? (
                                <div dangerouslySetInnerHTML={{ __html: formattedDescription }} />
                            ) : (
                                'N/A'
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Panel>
    );
};